import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { IndianRupee, ShoppingBag, Store, Package, TrendingUp, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/DateRangePicker';
import { ExportButton } from '@/components/ExportButton';
import { GeoFilters } from '@/components/GeoFilters';
import { DataFreshnessBanner } from '@/components/DataFreshnessBanner';
import { formatChartLabel, formatDayMonth, formatDisplayDate } from '@/utils/dateFormat';
import { useNavigate } from 'react-router-dom';

function KPICard({ label, value, icon: Icon, color = 'primary', delay = 0 }) {
  return (
    <div className="bg-card border border-border rounded-md p-5 hover:-translate-y-0.5 transition-transform duration-200 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="kpi-label text-muted-foreground">{label}</p>
          <p className="text-2xl font-heading font-bold tracking-tight text-foreground">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${color}/10`}>
          <Icon size={20} strokeWidth={1.5} className={`text-${color}`} />
        </div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{formatChartLabel(label)}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? Math.round(p.value).toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
}

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

export default function OverviewPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (zone) params.zone = zone;
        if (subzone) params.subzone = subzone;
        if (state) params.state = state;
        if (city) params.city = city;
        const [overviewRes, trendsRes] = await Promise.all([
          api.get('/overview', { params }),
          api.get('/overview/trends', { params }),
        ]);
        setData(overviewRes.data);
        setTrends(trendsRes.data);
      } catch (err) {
        toast.error('Failed to load overview data');
      }
      // Load alerts separately — don't let it break overview
      try {
        const alertsRes = await api.get('/alerts');
        setAlerts(alertsRes.data.alerts || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [api, dateFrom, dateTo, zone, subzone, state, city]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Loading dashboard...</p></div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">No data available. Upload CSV files in Configuration.</div>;

  const k = data.kpis;

  return (
    <div className="space-y-8 overflow-hidden" data-testid="overview-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Sales performance dashboard for Keya Foods</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <ExportButton module="overview" params={{ zone, subzone, state, city, date_from: dateFrom, date_to: dateTo }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone} state={state} setState={setState} city={city} setCity={setCity} />
      </div>

      <DataFreshnessBanner api={api} />

      {/* Alert Banner */}
      {alerts.length > 0 && (
        <button onClick={() => navigate('/alerts')} data-testid="alert-banner"
          className="w-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 flex items-center gap-3 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors duration-150 text-left overflow-hidden">
          <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
            <span className="text-amber-700 dark:text-amber-300 font-heading font-bold text-sm">{alerts.length}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {alerts.filter(a => a.severity === 'critical').length > 0 && `${alerts.filter(a => a.severity === 'critical').length} critical, `}
              {alerts.length} alert{alerts.length !== 1 ? 's' : ''} detected
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 truncate">
              {alerts[0]?.message}
            </p>
          </div>
        </button>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KPICard label="Order Value" value={`INR ${formatINR(k.total_sales)}`} icon={IndianRupee} color="primary" delay={0} />
        <KPICard label="Total Orders" value={k.order_count?.toLocaleString('en-IN')} icon={ShoppingBag} color="secondary" delay={50} />
        <KPICard label="Active Outlets" value={`${k.productive_outlets?.toLocaleString('en-IN')} / ${k.total_shops?.toLocaleString('en-IN')}`} icon={Store} color="primary" delay={100} />
        <KPICard label="Fulfilment Rate" value={`${k.fulfilment_rate}%`} icon={Percent} color="secondary" delay={150} />
        <KPICard label="Total SKUs" value={k.total_skus} icon={Package} color="primary" delay={200} />
        <KPICard label="Fulfilled" value={`INR ${formatINR(k.total_fulfilled)}`} icon={TrendingUp} color="secondary" delay={250} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-hidden">
        {/* Sales Trend */}
        <div className="bg-card border border-border rounded-md p-5 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-baseline justify-between gap-2 mb-4">
            <h3 className="font-heading font-semibold text-sm">Daily Sales Trend</h3>
            {trends.length > 0 && (
              <span className="text-[11px] text-muted-foreground" data-testid="sales-trend-range">
                {formatDisplayDate(trends[0].date)} to {formatDisplayDate(trends[trends.length - 1].date)} · order value
              </span>
            )}
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatDayMonth} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="value" name="Sales (INR)" stroke="#1B3A8C" fill="#1B3A8C" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Orders by Category */}
        <div className="bg-card border border-border rounded-md p-5 animate-fade-in-up" style={{ animationDelay: '350ms' }}>
          <h3 className="font-heading font-semibold text-sm mb-4">Orders by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sales_by_category} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Amount (INR)" fill="#B8974A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Orders by Zone */}
        <div className="bg-card border border-border rounded-md p-5 animate-fade-in-up lg:col-span-2" style={{ animationDelay: '400ms' }}>
          <h3 className="font-heading font-semibold text-sm mb-4">Orders by Zone</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sales_by_city}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Sales (INR)" fill="#1B3A8C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
